// Added by Sejune Oh on 2019-07-03
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC, Fragment, RefObject } from 'react'
import { withStyles, Theme, createStyles, WithStyles } from '@material-ui/core'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { ReactEmptyEvent, ReactInputChangeEvent, ReactKeyUpEvent } from '@/helper/define'
import Intl from '@/helper/Intl'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import {
    TextField,
    InputAdornment,
    LockIcon,
    VisibilityIcon,
    VisibilityOffIcon,
    Button,
    AccountCircleIcon,
    Typography,
    InfoIcon,
} from '@/components/_primes'

import LockScreenStyle from '@/styles/login/LockScreenStyle'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface ScreenLockFCProps extends WithStyles<typeof styles> {
    onUnlockBtnClick: ReactEmptyEvent
    onLogoutBtnClick: ReactEmptyEvent
    userId: string
    onPasswordInputIconClick: ReactEmptyEvent
    hidePassword: boolean
    onValueChange: ReactInputChangeEvent
    onKeyUp: ReactKeyUpEvent
    btnId: string
    productBI: string
    useLoginFormTopImage: boolean
    loginTopImage: string
    onLogoutMode: boolean
    onChangeLogoutModeBtnClick: (mode: boolean) => () => void
    loginBackgroundImage: string
    inputRef: RefObject<HTMLInputElement>
    version: string
    copyRight: string
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const ScreenLock: FC<ScreenLockFCProps> = ({ classes, ...props }) => {
    const descTitle = Intl.getIntlText({ id: 'htmljs_react.component.screenlock.desc_title' })
    const descContent = Intl.getIntlText({ id: 'htmljs_react.component.screenlock.desc_content' })
    const labelId = Intl.getIntlText({ id: 'htmljs_react.component.screenlock.label_id' })
    const holderId = Intl.getIntlText({ id: 'htmljs_react.component.screenlock.holder_id' })
    const labelPw = Intl.getIntlText({ id: 'htmljs_react.component.screenlock.label_pw' })
    const btnConfirm = props.onLogoutMode
        ? Intl.getIntlText({ id: 'htmljs_react.component.screenlock.btn_ok' })
        : Intl.getIntlText({ id: 'htmljs_react.component.screenlock.btn_unlock' })
    const btnCancel = props.onLogoutMode
        ? Intl.getIntlText({ id: 'htmljs_react.component.screenlock.btn_cancel' })
        : Intl.getIntlText({ id: 'htmljs_react.component.screenlock.btn_logout' })
    const descLogout = Intl.getIntlText({ id: 'htmljs_react.component.screenlock.desc_logout' })

    return (
        <div className={classes.Wrapper}>
            <div className={classes.BGWrapper} style={{ backgroundImage: props.loginBackgroundImage }}></div>
            <div className={classes.ScreenLockWrapper}>
                <div
                    className={classes.ScreenLockLeftWrapper}
                    style={{ backgroundImage: `url(/images/lock.form.left.template_v1.png)` }}
                ></div>
                <form className={classes.ScreenLockRightWrapper}>
                    <div className={classes.TextFieldWrapper}>
                        <div className={classes.HeaderWrapper}>
                            {props.useLoginFormTopImage ? (
                                <div
                                    className={classes.CPLogoImgWrapper}
                                    style={{ backgroundImage: props.loginTopImage }}
                                />
                            ) : (
                                    <div className={classes.CPLogo}>{props.productBI}</div>
                                )}
                        </div>
                        {props.onLogoutMode ? (
                            <div className={classes.DescTextWrapper}>
                                {descLogout.split('/n').map((t, i) => (
                                    <Typography key={`logout-desc-${i}`}>{t}</Typography>
                                ))}
                            </div>
                        ) : (
                                <Fragment>
                                    <TextField
                                        id="email"
                                        className={classes.Input}
                                        label={labelId}
                                        placeholder={holderId}
                                        margin="normal"
                                        variant="outlined"
                                        type="text"
                                        name="email"
                                        autoComplete="email nickname"
                                        value={props.userId}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AccountCircleIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                        inputProps={{
                                            className: classes.AutoFocusableInput,
                                        }}
                                        disabled
                                    />
                                    <TextField
                                        id="password"
                                        className={classes.Input}
                                        label={labelPw}
                                        margin="normal"
                                        variant="outlined"
                                        name="password"
                                        onChange={props.onValueChange}
                                        autoComplete="current-password"
                                        autoFocus={true}
                                        type={props.hidePassword ? 'password' : 'text'}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LockIcon />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="start" onClick={props.onPasswordInputIconClick}>
                                                    {props.hidePassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                </InputAdornment>
                                            ),
                                            onKeyUp: props.onKeyUp,
                                        }}
                                        inputProps={{
                                            className: classes.AutoFocusableInput,
                                        }}
                                        inputRef={props.inputRef}
                                    />
                                </Fragment>
                            )}
                    </div>

                    <div className={classes.BottomWrapper}>
                        <div className={classes.BtnWrapper}>
                            <Button
                                id={props.btnId}
                                btnText={btnConfirm}
                                className={classes.LockBtn}
                                buttonType={'task'}
                                onClick={props.onLogoutMode ? props.onLogoutBtnClick : props.onUnlockBtnClick}
                            />
                            <Button
                                btnText={btnCancel}
                                className={classes.LockBtn}
                                onClick={props.onChangeLogoutModeBtnClick.apply(null, [!props.onLogoutMode])}
                            />
                        </div>
                        <div className={classes.PWCheckDescWrapper}>
                            <div className={classes.PWCheckDescTitleWrapper}>
                                <InfoIcon className={classes.PWCheckDescIcon} />
                                <span className={classes.PWCheckDescTitle}>{descTitle}</span>
                            </div>
                            <div className={classes.PWCheckDescContent}>{descContent}</div>
                        </div>
                    </div>
                </form>

                <Typography className={classes.LoginPaperCopyRight} align="center">
                    {props.copyRight} ({props.version})
                </Typography>
            </div>
        </div>
    )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const styles = (theme: Theme) =>
    createStyles({
        ...LockScreenStyle(theme),
    })

export default withStyles(styles, { withTheme: true })(ScreenLock)
